// =============================================
// SERVICE: API de Montagem de Paletes
// =============================================

import { apiService } from './api';
import { buildApiUrl } from './config';
import type {
  Filial,
  AreaEntrega,
  AreaCodigoCliente,
  MontagemPaleteOperacao,
  PaleteItem,
  MontagemAgendamento,
  MontagemEstatisticas,
  OperacaoCreateData,
  AgendamentoCreateData,
  AgendamentoUpdateData
} from '../types/montagemPalete';

function buildUrlWithParams(path: string, params?: Record<string, unknown>): string {
  const url = buildApiUrl(path);
  if (!params) return url;
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, String(value));
    }
  }
  const qs = searchParams.toString();
  return qs ? `${url}?${qs}` : url;
}

// =============================================
// FILIAIS
// =============================================

export async function listarFiliais(params?: { ativo?: string; busca?: string }) {
  return apiService.request<{ success: boolean; filiais: Filial[] }>(
    buildUrlWithParams('/montar-carga/filiais', params as Record<string, unknown>)
  );
}

export async function buscarFilial(id: number) {
  return apiService.request<{ success: boolean; filial: Filial }>(
    buildApiUrl(`/montar-carga/filiais/${id}`)
  );
}

export async function criarFilial(data: Partial<Filial>) {
  return apiService.request<{ success: boolean; filial: Filial }>(
    buildApiUrl('/montar-carga/filiais'), { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function atualizarFilial(id: number, data: Partial<Filial>) {
  return apiService.request<{ success: boolean; filial: Filial }>(
    buildApiUrl(`/montar-carga/filiais/${id}`), { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function deletarFilial(id: number) {
  return apiService.request<{ success: boolean; message: string }>(
    buildApiUrl(`/montar-carga/filiais/${id}`), { method: 'DELETE' }
  );
}

// =============================================
// ÁREAS DE ENTREGA
// =============================================

export async function listarAreas(params?: { ativo?: string; filial_id?: string | number; busca?: string; cliente_id?: string | number }) {
  return apiService.request<{ success: boolean; areas: AreaEntrega[] }>(
    buildUrlWithParams('/montar-carga/areas', params as Record<string, unknown>)
  );
}

export async function buscarArea(id: number) {
  return apiService.request<{ success: boolean; area: AreaEntrega; codigos_cliente: AreaCodigoCliente[] }>(
    buildApiUrl(`/montar-carga/areas/${id}`)
  );
}

export async function criarArea(data: Partial<AreaEntrega>) {
  return apiService.request<{ success: boolean; area: AreaEntrega }>(
    buildApiUrl('/montar-carga/areas'), { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function atualizarArea(id: number, data: Partial<AreaEntrega>) {
  return apiService.request<{ success: boolean; area: AreaEntrega }>(
    buildApiUrl(`/montar-carga/areas/${id}`), { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function deletarArea(id: number) {
  return apiService.request<{ success: boolean; message: string }>(
    buildApiUrl(`/montar-carga/areas/${id}`), { method: 'DELETE' }
  );
}

// =============================================
// CÓDIGOS DE ÁREA POR CLIENTE
// =============================================

export async function listarCodigosArea(areaId: number) {
  return apiService.request<{ success: boolean; codigos: AreaCodigoCliente[] }>(
    buildApiUrl(`/montar-carga/areas/${areaId}/codigos`)
  );
}

export async function adicionarCodigoArea(areaId: number, data: { cliente_id: number; codigo: string }) {
  return apiService.request<{ success: boolean; codigo: AreaCodigoCliente }>(
    buildApiUrl(`/montar-carga/areas/${areaId}/codigos`),
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function atualizarCodigoArea(codigoId: number, data: { cliente_id: number; codigo: string }) {
  return apiService.request<{ success: boolean; codigo: AreaCodigoCliente }>(
    buildApiUrl(`/montar-carga/areas/codigos/${codigoId}`),
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function removerCodigoArea(codigoId: number) {
  return apiService.request<{ success: boolean; message: string }>(
    buildApiUrl(`/montar-carga/areas/codigos/${codigoId}`), { method: 'DELETE' }
  );
}

export async function salvarCodigosAreaBatch(areaId: number, codigos: { cliente_id: number; codigo: string }[]) {
  return apiService.request<{ success: boolean; codigos: AreaCodigoCliente[]; total: number }>(
    buildApiUrl(`/montar-carga/areas/${areaId}/codigos/batch`),
    { method: 'POST', body: JSON.stringify({ codigos }) }
  );
}

export async function identificarArea(codigo: string, clienteId: number) {
  return apiService.request<{
    success: boolean;
    encontrado: boolean;
    tipo: 'principal' | 'secundario';
    area: { id: number; nome: string; codigo: string; regiao: string };
  }>(buildUrlWithParams('/montar-carga/areas/identificar', {
    codigo, cliente_id: clienteId
  } as Record<string, unknown>));
}

// =============================================
// OPERAÇÕES
// =============================================

export async function listarOperacoes(params?: {
  page?: number; limit?: number; status?: string; cliente_id?: string | number;
  filial_id?: string | number; dataInicio?: string; dataFim?: string; busca?: string;
  numero_pedido?: string;
}) {
  return apiService.request<{
    success: boolean;
    operacoes: MontagemPaleteOperacao[];
    pagination: { page: number; totalPages: number; total: number; hasNext: boolean; hasPrev: boolean };
  }>(buildUrlWithParams('/montar-carga/operacoes', params as Record<string, unknown>));
}

export async function buscarOperacao(id: number) {
  return apiService.request<{
    success: boolean;
    operacao: MontagemPaleteOperacao;
    paletes: PaleteItem[];
  }>(buildApiUrl(`/montar-carga/operacoes/${id}`));
}

export async function criarOperacao(data: OperacaoCreateData) {
  return apiService.request<{
    success: boolean;
    operacao: MontagemPaleteOperacao;
    paletes: PaleteItem[];
  }>(buildApiUrl('/montar-carga/operacoes'), { method: 'POST', body: JSON.stringify(data) });
}

export async function atualizarOperacao(id: number, data: Partial<MontagemPaleteOperacao>) {
  return apiService.request<{ success: boolean; operacao: MontagemPaleteOperacao }>(
    buildApiUrl(`/montar-carga/operacoes/${id}`), { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function deletarOperacao(id: number) {
  return apiService.request<{ success: boolean; message: string }>(
    buildApiUrl(`/montar-carga/operacoes/${id}`), { method: 'DELETE' }
  );
}

export async function copiarOperacao(id: number) {
  return apiService.request<{
    success: boolean;
    operacao: MontagemPaleteOperacao;
    paletes: PaleteItem[];
  }>(buildApiUrl(`/montar-carga/operacoes/${id}/copiar`), { method: 'POST' });
}

export async function obterEstatisticas(params?: { dataInicio?: string; dataFim?: string }) {
  return apiService.request<{ success: boolean; estatisticas: MontagemEstatisticas }>(
    buildUrlWithParams('/montar-carga/operacoes/estatisticas', params as Record<string, unknown>)
  );
}

// =============================================
// PALETES
// =============================================

export async function listarPaletes(operacaoId: number) {
  return apiService.request<{ success: boolean; paletes: PaleteItem[] }>(
    buildApiUrl(`/montar-carga/operacoes/${operacaoId}/paletes`)
  );
}

export async function adicionarPalete(operacaoId: number, data: {
  area_entrega_id: number; doca?: string; observacoes?: string; numero_palete?: number;
  tipo_palete?: string; data_agendamento?: string;
}) {
  return apiService.request<{ success: boolean; palete: PaleteItem }>(
    buildApiUrl(`/montar-carga/operacoes/${operacaoId}/paletes`),
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function atualizarPalete(paleteId: number, data: {
  tipo_palete?: string; data_agendamento?: string; doca?: string; observacoes?: string; status?: string;
}) {
  return apiService.request<{ success: boolean; palete: PaleteItem }>(
    buildApiUrl(`/montar-carga/paletes/${paleteId}`),
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function validarPalete(clienteId: number, numeroPalete: number, areaEntregaId?: number) {
  const params: Record<string, unknown> = {
    cliente_id: clienteId,
    numero_palete: numeroPalete
  };
  if (areaEntregaId) params.area_entrega_id = areaEntregaId;
  return apiService.request<{
    success: boolean;
    palete: {
      id: number;
      numero_palete: number;
      operacao_id: number;
      numero_operacao: number;
      area_entrega_id: number;
      area_nome: string;
      area_codigo: string;
      area_regiao: string;
      doca: string | null;
      tipo_palete?: string;
      data_agendamento?: string;
    };
  }>(buildUrlWithParams('/montar-carga/operacoes/validar-palete', params));
}

export async function obterProximoNumeroPalete() {
  return apiService.request<{ success: boolean; proximo_numero: number }>(
    buildApiUrl('/montar-carga/paletes/proximo-numero')
  );
}

export async function gerarNovoPalete(params?: {
  clienteId?: number;
  areaEntregaId?: number;
  tipoPalete?: string;
  dataAgendamento?: string;
}): Promise<{
  success: boolean;
  precisa_selecionar_cliente: boolean;
  precisa_selecionar_area?: boolean;
  precisa_selecionar_tipo_palete?: boolean;
  clientes?: { id: number; razao_social: string; cnpj: string }[];
  areas?: { id: number; nome: string; codigo: string | null; regiao: string | null; filial_id: number | null }[];
  area_entrega_id?: number;
  palete?: {
    id: number;
    numero_palete: number;
    doca: string | null;
    area_entrega_id: number;
    area_nome: string;
    area_codigo: string;
    area_regiao: string;
    operacao_id: number;
    numero_operacao: number;
    tipo_palete?: string;
    data_agendamento?: string;
  };
  cliente?: {
    id: number;
    razao_social: string;
    cnpj: string;
  };
  etiqueta_configs?: any[];
  etiqueta_modelos?: any[];
  leituras?: any[];
  total_volumes?: number;
}> {
  const body: Record<string, unknown> = {};
  if (params?.clienteId) body.cliente_id = params.clienteId;
  if (params?.areaEntregaId) body.area_entrega_id = params.areaEntregaId;
  if (params?.tipoPalete) body.tipo_palete = params.tipoPalete;
  if (params?.dataAgendamento) body.data_agendamento = params.dataAgendamento;
  return apiService.request(
    buildApiUrl('/montar-carga/paletes/gerar-conferente'),
    { method: 'POST', body: JSON.stringify(body) }
  );
}

export async function removerPalete(paleteId: number) {
  return apiService.request<{ success: boolean; message: string }>(
    buildApiUrl(`/montar-carga/paletes/${paleteId}`), { method: 'DELETE' }
  );
}

// =============================================
// LISTAGEM GLOBAL DE PALETES
// =============================================

export interface PaleteListItem {
  id: number;
  numero_palete: number;
  operacao_id: number;
  area_entrega_id: number;
  doca: string | null;
  tipo_palete: string;
  status: string;
  data_agendamento: string | null;
  observacoes: string | null;
  criado_em: string;
  atualizado_em: string;
  area_nome: string;
  area_codigo: string;
  area_regiao: string;
  numero_operacao: number;
  operacao_status: string;
  cliente_id: number;
  cliente_razao_social: string;
  cliente_cnpj: string;
  total_leituras: number;
  leituras_ok: number;
  leituras_duplicadas: number;
  total_pedidos: number;
  peso_total: number;
  total_ocorrencias: number;
}

export interface PaleteDetalhadoEtiqueta {
  id: number;
  codigo_lido: string;
  resultado: string;
  tipo_leitura: string;
  dados_decodificados: Record<string, unknown>;
  lido_em: string;
  criado_em: string;
  conferencia_id: number;
  conferente_nome?: string;
}

export interface PaleteDetalhadoPedido {
  numero_pedido: string;
  total_volumes_esperado: number;
  total_lido: number;
  total_duplicados: number;
  total_removidos: number;
  volumes_removidos_unicos: number;
  etiquetas: PaleteDetalhadoEtiqueta[];
}

export interface PaleteDetalhadoOcorrencia {
  id: number;
  tipo_ocorrencia_id: number;
  tipo_nome: string;
  categoria: string;
  justificativa: string;
  quantidade_divergente: number;
  criado_em: string;
  numero_pedido: string;
}

export interface PaleteDetalhadaEtiquetaPendente {
  id: number;
  codigo_barras: string;
  numero_pedido: string;
  volume_atual: number | null;
  total_volumes_pedido: number | null;
  status: string;
  numero_nfe: string | null;
  destinatario_nome: string | null;
  destinatario_cnpj: string | null;
}

export interface PaleteDetalhado extends PaleteListItem {
  resumo: {
    total_leituras: number;
    leituras_ok: number;
    leituras_duplicadas: number;
    leituras_removidas: number;
    peso_total: number;
    total_pedidos: number;
  };
  pedidos: PaleteDetalhadoPedido[];
  ocorrencias: PaleteDetalhadoOcorrencia[];
  etiquetas_pendentes: PaleteDetalhadaEtiquetaPendente[];
}

export interface PaleteEstatisticas {
  total_paletes: number;
  total_clientes: number;
  total_operacoes: number;
}

export async function listarTodosPaletes(params?: {
  page?: number; limit?: number; busca?: string; cliente_id?: string | number;
  operacao_id?: string | number; area_entrega_id?: string | number; status_operacao?: string;
  status_palete?: string; numero_pedido?: string;
  data_inicio?: string; data_fim?: string;
}) {
  return apiService.request<{
    success: boolean;
    paletes: PaleteListItem[];
    pagination: { page: number; totalPages: number; total: number; hasNext: boolean; hasPrev: boolean };
  }>(buildUrlWithParams('/montar-carga/paletes/todos', params as Record<string, unknown>));
}

export interface RelatorioExportRow {
  numero_palete: number | null;
  numero_operacao: number | null;
  cliente: string | null;
  cliente_cnpj: string | null;
  area_codigo: string | null;
  area_nome: string | null;
  numero_pedido: string | null;
  codigo_lido: string;
  volume_atual: string | null;
  total_volumes: string | null;
  peso: string | null;
  resultado: string;
  tipo_leitura: string;
  conferente_nome: string | null;
  criado_em: string;
  status_palete: string | null;
  status_operacao: string | null;
}

export async function exportarRelatorioConferencia(params?: {
  data_inicio?: string;
  data_fim?: string;
  cliente_id?: string | number;
  numero_pedido?: string;
  busca?: string;
}) {
  return apiService.request<{ success: boolean; dados: RelatorioExportRow[] }>(
    buildUrlWithParams('/montar-carga/paletes/relatorio/export', params as Record<string, unknown>)
  );
}

export async function buscarPaleteDetalhado(
  id: number,
  filtros?: { data_inicio?: string; data_fim?: string }
) {
  return apiService.request<{
    success: boolean;
    palete: PaleteDetalhado;
  }>(buildUrlWithParams(`/montar-carga/paletes/todos/${id}`, filtros as Record<string, unknown>));
}

export async function obterEstatisticasPaletes() {
  return apiService.request<{
    success: boolean;
    estatisticas: PaleteEstatisticas;
  }>(buildApiUrl('/montar-carga/paletes/todos/estatisticas'));
}

// =============================================
// AGENDAMENTOS
// =============================================

export async function listarAgendamentos() {
  return apiService.request<{ success: boolean; agendamentos: MontagemAgendamento[] }>(
    buildApiUrl('/montar-carga/agendamentos')
  );
}

export async function buscarAgendamento(id: number) {
  return apiService.request<{ success: boolean; agendamento: MontagemAgendamento }>(
    buildApiUrl(`/montar-carga/agendamentos/${id}`)
  );
}

export async function criarAgendamento(data: AgendamentoCreateData) {
  return apiService.request<{ success: boolean; agendamento: MontagemAgendamento }>(
    buildApiUrl('/montar-carga/agendamentos'), { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function atualizarAgendamento(id: number, data: AgendamentoUpdateData) {
  return apiService.request<{ success: boolean; agendamento: MontagemAgendamento }>(
    buildApiUrl(`/montar-carga/agendamentos/${id}`), { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function deletarAgendamento(id: number) {
  return apiService.request<{ success: boolean; message: string }>(
    buildApiUrl(`/montar-carga/agendamentos/${id}`), { method: 'DELETE' }
  );
}

// =============================================
// ENVIO AUTOMÁTICO
// =============================================

export async function listarEnviosAutomaticos(agendamentoId: number) {
  return apiService.request<{ success: boolean; data: any[] }>(
    buildApiUrl(`/montar-carga/envio-automatico/agendamento/${agendamentoId}`)
  );
}

export async function salvarEnviosAgendamento(agendamentoId: number, envios: any[]) {
  return apiService.request<{ success: boolean; data: any[]; message: string }>(
    buildApiUrl(`/montar-carga/envio-automatico/agendamento/${agendamentoId}/salvar`),
    { method: 'POST', body: JSON.stringify({ envios }) }
  );
}

export async function criarEnvioAutomatico(data: any) {
  return apiService.request<{ success: boolean; data: any; message: string }>(
    buildApiUrl('/montar-carga/envio-automatico'),
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function deletarEnvioAutomatico(id: number) {
  return apiService.request<{ success: boolean; message: string }>(
    buildApiUrl(`/montar-carga/envio-automatico/${id}`), { method: 'DELETE' }
  );
}

export async function enviarDocumentoOperacao(operacaoId: number, envios: any[]) {
  return apiService.request<{ success: boolean; data: any[]; message: string }>(
    buildApiUrl(`/montar-carga/operacoes/${operacaoId}/enviar-documento`),
    { method: 'POST', body: JSON.stringify({ envios }) }
  );
}

export async function enviarDocumentoPalete(paleteId: number, envios: any[]) {
  return apiService.request<{ success: boolean; data: any[]; message: string }>(
    buildApiUrl(`/montar-carga/paletes/${paleteId}/enviar-documento`),
    { method: 'POST', body: JSON.stringify({ envios }) }
  );
}

export async function salvarEnviosOperacao(operacaoId: number, envios: any[]) {
  return apiService.request<{ success: boolean; data: any[]; message: string }>(
    buildApiUrl(`/montar-carga/envio-automatico/operacao/${operacaoId}/salvar`),
    { method: 'POST', body: JSON.stringify({ envios }) }
  );
}

export async function listarEnviosOperacao(operacaoId: number) {
  return apiService.request<{ success: boolean; data: any[] }>(
    buildApiUrl(`/montar-carga/envio-automatico/operacao/${operacaoId}`)
  );
}

// =============================================
// EMAIL CONNECTIONS
// =============================================

export async function listarEmailConnections() {
  return apiService.request<{ sucesso: boolean; dados: any[] }>(
    buildApiUrl('/email-connections')
  );
}

export async function criarEmailConnection(data: any) {
  return apiService.request<{ sucesso: boolean; dados: any; mensagem: string }>(
    buildApiUrl('/email-connections'),
    { method: 'POST', body: JSON.stringify(data) }
  );
}

export async function atualizarEmailConnection(id: number, data: any) {
  return apiService.request<{ sucesso: boolean; dados: any; mensagem: string }>(
    buildApiUrl(`/email-connections/${id}`),
    { method: 'PUT', body: JSON.stringify(data) }
  );
}

export async function deletarEmailConnection(id: number) {
  return apiService.request<{ sucesso: boolean; mensagem: string }>(
    buildApiUrl(`/email-connections/${id}`), { method: 'DELETE' }
  );
}

export async function testarEmailConnection(id: number | string, data?: any) {
  const url = id === 'test' ? '/email-connections/test' : `/email-connections/${id}/test`;
  return apiService.request<{ sucesso: boolean; mensagem: string }>(
    buildApiUrl(url),
    { method: 'POST', body: data ? JSON.stringify(data) : undefined }
  );
}
